import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { api } from "./client";
import type {
  CameraList,
  ConnectionTestResponse,
  Site,
  SiteCreateInput,
  SiteHealth,
  SiteList,
} from "@/types";

const sitesKey = ["sites"] as const;

export function useSites(opts?: { includeInactive?: boolean }) {
  return useQuery({
    queryKey: [...sitesKey, "list", opts ?? {}],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (opts?.includeInactive) params.set("include_inactive", "true");
      const { data } = await api.get<SiteList>(
        `/api/v1/sites?${params.toString()}`,
      );
      return data;
    },
  });
}

export function useSite(id: string | undefined) {
  return useQuery({
    queryKey: [...sitesKey, "detail", id],
    queryFn: async () => {
      const { data } = await api.get<Site>(`/api/v1/sites/${id}`);
      return data;
    },
    enabled: Boolean(id),
  });
}

export function useSiteHealth(id: string | undefined) {
  return useQuery({
    queryKey: [...sitesKey, "health", id],
    queryFn: async () => {
      const { data } = await api.get<SiteHealth>(`/api/v1/sites/${id}/health`);
      return data;
    },
    enabled: Boolean(id),
    refetchInterval: 30_000,
  });
}

export function useSiteCameras(id: string | undefined) {
  return useQuery({
    queryKey: [...sitesKey, "cameras", id],
    queryFn: async () => {
      const { data } = await api.get<CameraList>(
        `/api/v1/sites/${id}/cameras?limit=200`,
      );
      return data;
    },
    enabled: Boolean(id),
  });
}

export function useCreateSite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: SiteCreateInput) => {
      const { data } = await api.post<Site>("/api/v1/sites", input);
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [...sitesKey, "list"] });
    },
  });
}

export function useUpdateSite(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (patch: Partial<SiteCreateInput> & { is_active?: boolean }) => {
      const { data } = await api.patch<Site>(`/api/v1/sites/${id}`, patch);
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [...sitesKey, "detail", id] });
      void qc.invalidateQueries({ queryKey: [...sitesKey, "list"] });
    },
  });
}

export function useDeactivateSite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/api/v1/sites/${id}`);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: sitesKey });
    },
  });
}


// ---------------------------------------------------------------------------
// Sprint 10 fix — NVR config on site + site-level health poll
// ---------------------------------------------------------------------------
export interface SiteNVRConfigInput {
  nvr_host?: string;
  nvr_port?: number;
  nvr_user?: string;
  nvr_pass?: string;
}

export interface NVRHealthSnapshotT {
  id: string;
  site_id: string | null;
  camera_id: string | null;
  checked_at: string;
  hdd_count: number | null;
  hdd_total_gb: number | null;
  hdd_used_gb: number | null;
  hdd_status: "ok" | "warning" | "error" | "unknown" | null;
  hdd_smart_status: "ok" | "warning" | "failed" | "unknown" | null;
  nvr_uptime_sec: number | null;
  cpu_pct: number | null;
  mem_pct: number | null;
  recording_channels_active: number | null;
  stream_status: "ready" | "starting" | "offline" | "unknown";
  error_message: string | null;
}

export interface PollHealthQueued {
  status: "queued";
  task_id: string;
  poll_url: string;
}

// FIX C — POST returns 202 + {status, task_id, poll_url}; callers should
// then poll /health/latest until checked_at > click_ts.
export function usePollSiteHealth() {
  return useMutation({
    mutationFn: async (siteId: string) => {
      const { data } = await api.post<PollHealthQueued>(
        `/api/v1/sites/${siteId}/health/poll`,
      );
      return data;
    },
  });
}

// FIX C — most recent health snapshot for a site. Poll with a custom
// interval when waiting for a Poll Now to land.
export function useLatestSiteHealth(
  siteId: string | undefined,
  opts?: { refetchMs?: number; enabled?: boolean },
) {
  return useQuery({
    queryKey: [...sitesKey, "health-latest", siteId],
    queryFn: async () => {
      const { data } = await api.get<NVRHealthSnapshotT>(
        `/api/v1/sites/${siteId}/health/latest`,
      );
      return data;
    },
    enabled: Boolean(siteId) && (opts?.enabled ?? true),
    refetchInterval: opts?.refetchMs ?? 60_000,
    retry: false,
  });
}

// S11 P4 — protocol-aware liveness probe.
//
// Wraps POST /sites/{id}/test-connection. The endpoint dispatches via the
// vendor-agnostic protocol factory, so the response shape is the same
// regardless of the site's protocol (ISAPI / ONVIF / Dahua / RTSP).
// For RTSP, ``camera_id`` is required — pass it as a query param.
export function useTestSiteConnection(siteId: string) {
  return useMutation({
    mutationFn: async (opts?: { cameraId?: string }) => {
      const qs = opts?.cameraId ? `?camera_id=${opts.cameraId}` : "";
      const { data } = await api.post<ConnectionTestResponse>(
        `/api/v1/sites/${siteId}/test-connection${qs}`,
      );
      return data;
    },
  });
}

export function useSiteHealthHistory(siteId: string | undefined, limit = 48) {
  return useQuery({
    queryKey: [...sitesKey, "health-history", siteId, limit],
    queryFn: async () => {
      const { data } = await api.get<{
        camera_id: string;
        items: NVRHealthSnapshotT[];
      }>(`/api/v1/sites/${siteId}/health/history?limit=${limit}`);
      return data;
    },
    enabled: Boolean(siteId),
    refetchInterval: 5 * 60 * 1000, // 5 min — server-side poll is 5min cadence
  });
}
